import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../context/AuthContext';
import { useEntitlements } from '../context/EntitlementsContext';
import { useUpgrade } from '../context/UpgradeContext';
import {
    loadCachedProjects,
    saveCachedProjects,
    enqueueMutation,
    getQueue,
    getQueueLength,
    removeFromQueue,
    isOfflineError,
} from '../lib/offlineCache';

/** Generate a client-side temp id for optimistic records. The `temp_` prefix
 *  lets us distinguish records that are still queued from server-issued ones. */
function makeTempId(prefix) {
    return `${prefix}_temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Convert a base64 dataURL to a Blob we can POST as multipart form data. */
function dataURLToBlob(dataURL) {
    if (!dataURL || typeof dataURL !== 'string' || !dataURL.startsWith('data:')) return null;
    const [header, body] = dataURL.split(',');
    const mime = /data:([^;]+);/.exec(header)?.[1] || 'application/octet-stream';
    const bin = atob(body);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
}

/** Hydrate floor/snag media paths into URLs the `<img>` tag can use directly.
 *  Always use a same-origin relative path for the `<img>` so the session_token
 *  cookie travels — even if REACT_APP_BACKEND_URL points at a different origin.
 *  The ingress routes `/api/*` back to the backend regardless. */
function resolveMedia(projects) {
    return projects.map(p => ({
        ...p,
        floorplans: (p.floorplans || []).map(f => ({
            ...f,
            image: f.image_path ? `/api/files/${f.image_path}` : (f.image || null),
        })),
        snags: (p.snags || []).map(s => ({
            ...s,
            photo: s.photoPath ? `/api/files/${s.photoPath}` : (s.photo || null),
        })),
    }));
}

/** Surface a 402 paywall response via toast for now; sub-phase 1C will add
 *  a proper upgrade modal. Returns the error so callers can rethrow. */
function handleApiError(err, fallbackMsg = 'Something went wrong') {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 402) {
        const msg = typeof detail === 'object' ? detail.message : String(detail || 'Upgrade required');
        toast.error(msg);
        const e = new Error(msg);
        e.code = typeof detail === 'object' ? detail.code : 'payment_required';
        e.status = 402;
        throw e;
    }
    toast.error(fallbackMsg);
    throw err;
}

/** Drop-in replacement for the old localStorage useStore(userId). Exposes the
 *  same mutator surface so pages don't need any structural refactor. */
export function useProjectsAPI(enabled = true) {
    const [state, setState] = useState({ projects: [] });
    const [ready, setReady] = useState(false);
    const ent = useEntitlements();
    const upgrade = useUpgrade();
    const { user } = useAuth();
    const userId = user?.user_id || null;
    const drainingRef = useRef(false);

    /** Persist to IDB + emit queue-count change so the header indicator stays live. */
    const broadcastQueueCount = useCallback(async () => {
        const count = await getQueueLength();
        window.dispatchEvent(new CustomEvent('defectr:queue:change', { detail: { count } }));
    }, []);

    const refresh = useCallback(async () => {
        if (!enabled) { setReady(true); return []; }
        try {
            const res = await axios.get(`${API}/properties`, { withCredentials: true });
            const projs = resolveMedia(res.data.properties || []);
            setState({ projects: projs });
            // Persist a fresh snapshot — keyed by user id so a sign-out
            // doesn't leak the previous user's data into a new session.
            if (userId) saveCachedProjects(userId, projs);
            return projs;
        } catch (err) {
            // Network down → fall back to the cached snapshot so the user
            // still sees their data and can keep working.
            if (isOfflineError(err) && userId) {
                const cached = await loadCachedProjects(userId);
                if (cached) {
                    setState({ projects: cached });
                    return cached;
                }
            }
            setState({ projects: [] });
            return [];
        } finally {
            setReady(true);
        }
    }, [enabled, userId]);

    /** Hydrate from the IDB snapshot FIRST (so the UI paints in <50ms),
     *  then trigger a network refresh in the background. Net effect: the
     *  app launches feeling instant, then live data slides in. */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!enabled || !userId) {
                refresh();
                return;
            }
            const cached = await loadCachedProjects(userId);
            if (cached && !cancelled) {
                setState({ projects: cached });
                setReady(true);
            }
            // Always do the live fetch — cache is a head-start, not the source of truth
            refresh();
        })();
        return () => { cancelled = true; };
    }, [enabled, userId, refresh]);

    // Initial queue count broadcast
    useEffect(() => { broadcastQueueCount(); }, [broadcastQueueCount]);

    /** Phase 2.5a follow-up — refresh on tab focus / visibility return so
     *  builder responses (Approve / Reject) surface in the homeowner's
     *  app the moment they switch back to it. Without this, a homeowner
     *  who left their tab open all day wouldn't see today's responses
     *  until they hard-refreshed. We rate-limit to once every 30 seconds
     *  so a flicky alt-tabbing user doesn't hammer /api/properties.
     */
    useEffect(() => {
        if (!enabled || !userId) return;
        let lastRefresh = 0;
        const MIN_GAP_MS = 30 * 1000;
        const onFocus = () => {
            if (document.visibilityState !== 'visible') return;
            if (!navigator.onLine) return;
            const now = Date.now();
            if (now - lastRefresh < MIN_GAP_MS) return;
            lastRefresh = now;
            refresh();
        };
        document.addEventListener('visibilitychange', onFocus);
        window.addEventListener('focus', onFocus);
        return () => {
            document.removeEventListener('visibilitychange', onFocus);
            window.removeEventListener('focus', onFocus);
        };
    }, [enabled, userId, refresh]);

    // ---------- Offline queue plumbing ------------------------------------
    /** Mutate a single snag inside `state.projects` immutably. Returns the
     *  new projects array so callers can decide whether to setState. */
    const localPatchSnag = useCallback((projectId, snagId, patch) => {
        return state.projects.map(p => p.id !== projectId ? p : ({
            ...p,
            snags: p.snags.map(s => s.id !== snagId ? s : ({ ...s, ...patch })),
        }));
    }, [state.projects]);

    /** Replay one queued mutation. Returns true on success, false on failure
     *  (so the drainer can stop and try again on next reconnect). */
    const replayMutation = useCallback(async (item) => {
        const { type, payload } = item;
        try {
            if (type === 'addSnag') {
                const { projectId, snag } = payload;
                const { photo, ...rest } = snag || {};
                const res = await axios.post(`${API}/properties/${projectId}/snags`, rest, { withCredentials: true });
                if (photo && res.data.snag?.id) {
                    try {
                        const blob = dataURLToBlob(photo);
                        if (blob) {
                            const fd = new FormData();
                            fd.append('file', blob, `photo.${blob.type.split('/')[1] || 'jpg'}`);
                            await axios.post(
                                `${API}/properties/${projectId}/snags/${res.data.snag.id}/photo`,
                                fd,
                                { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
                            );
                        }
                    } catch { /* photo failure is non-fatal — snag still synced */ }
                }
            } else if (type === 'updateSnag') {
                const { projectId, snagId, patch } = payload;
                const { photo, ...rest } = patch || {};
                if (Object.keys(rest).length > 0) {
                    await axios.patch(`${API}/properties/${projectId}/snags/${snagId}`, rest, { withCredentials: true });
                }
                if (photo) {
                    try {
                        const blob = dataURLToBlob(photo);
                        if (blob) {
                            const fd = new FormData();
                            fd.append('file', blob, `photo.${blob.type.split('/')[1] || 'jpg'}`);
                            await axios.post(
                                `${API}/properties/${projectId}/snags/${snagId}/photo`,
                                fd,
                                { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
                            );
                        }
                    } catch { /* non-fatal */ }
                }
            } else if (type === 'deleteSnag') {
                await axios.delete(`${API}/properties/${payload.projectId}/snags/${payload.snagId}`, { withCredentials: true });
            } else if (type === 'addRoom') {
                await axios.post(`${API}/properties/${payload.projectId}/rooms`, { name: payload.name }, { withCredentials: true });
            } else if (type === 'deleteRoom') {
                await axios.delete(`${API}/properties/${payload.projectId}/rooms/${payload.roomId}`, { withCredentials: true });
            }
            return true;
        } catch (err) {
            // If the server responded with a 4xx (bad data, snag deleted on
            // another device, etc) we still drain — there's no point holding
            // a permanently-failing item in the queue. Only network failures
            // get retried.
            if (isOfflineError(err)) return false;
            console.warn('[offline-sync] dropping unreplayable mutation', item, err);
            return true;
        }
    }, []);

    const drainQueue = useCallback(async () => {
        if (drainingRef.current) return;
        drainingRef.current = true;
        try {
            const items = await getQueue();
            if (items.length === 0) return;
            // Count what we actually pushed to the server so the post-sync
            // toast tells the user something concrete ("3 defects synced").
            const synced = { addSnag: 0, updateSnag: 0, deleteSnag: 0, addRoom: 0, deleteRoom: 0 };
            for (const item of items) {
                const ok = await replayMutation(item);
                if (!ok) break;  // stop on first failure — try again next event
                if (Object.prototype.hasOwnProperty.call(synced, item.type)) synced[item.type] += 1;
                await removeFromQueue(item.id);
                await broadcastQueueCount();
            }
            const remaining = await getQueueLength();
            if (remaining === 0 && navigator.onLine) await refresh();

            // Quietly celebrate the offline-first promise paying off.
            const totalSynced = Object.values(synced).reduce((a, b) => a + b, 0);
            if (totalSynced > 0) {
                const parts = [];
                const snagOps = synced.addSnag + synced.updateSnag + synced.deleteSnag;
                if (snagOps > 0) parts.push(`${snagOps} defect${snagOps === 1 ? '' : 's'}`);
                if (synced.addRoom + synced.deleteRoom > 0) {
                    const roomOps = synced.addRoom + synced.deleteRoom;
                    parts.push(`${roomOps} room${roomOps === 1 ? '' : 's'}`);
                }
                toast.success(`Synced ${parts.join(' & ')} from your last site visit.`, {
                    duration: 5000,
                    description: 'Everything you captured offline is now safely saved.',
                });
            }
        } finally {
            drainingRef.current = false;
        }
    }, [replayMutation, broadcastQueueCount, refresh]);

    // Run the drain whenever the browser tells us we're back online, plus
    // once on mount in case we have a queue from a previous session.
    useEffect(() => {
        const handler = () => drainQueue();
        window.addEventListener('online', handler);
        // Also drain on mount — the browser might already be online but we
        // could have items left over from a crash mid-sync.
        if (typeof navigator !== 'undefined' && navigator.onLine) drainQueue();
        return () => window.removeEventListener('online', handler);
    }, [drainQueue]);

    /** Helper: try to mutate optimistically + queue if offline.
     *  Returns { result, projects, queued } so callers can run post-sync
     *  side-effects (e.g. tier nudges) against the canonical fresh list. */
    const optimistic = useCallback(async ({
        nextProjects,    // (projects) => projects — local cache patch
        type,            // queue mutation type
        payload,         // queue payload (must be JSON-serialisable)
        tempId,          // optimistic record id (for matching during reconcile)
        run,             // async fn that performs the real network call
        errorMsg,
    }) => {
        // Apply optimistic local state immediately so the UI feels instant.
        const optimisticProjects = nextProjects(state.projects);
        setState({ projects: optimisticProjects });
        if (userId) saveCachedProjects(userId, optimisticProjects);

        try {
            const result = await run();
            const fresh = await refresh();
            return { result, projects: fresh, queued: false };
        } catch (err) {
            if (isOfflineError(err)) {
                await enqueueMutation(type, payload, tempId);
                await broadcastQueueCount();
                toast.success('Saved locally. Will sync when you\'re back online.');
                return { result: null, projects: optimisticProjects, queued: true };
            }
            // Real HTTP error → roll back the optimistic change by reloading
            const fresh = await refresh();
            if (!handle402(err, { projectId: payload.projectId })) handleApiError(err, errorMsg);
            return { result: null, projects: fresh, queued: false };
        }
    }, [state.projects, userId, refresh, broadcastQueueCount]);

    // Intercept 402s and auto-open the Upgrade modal against the relevant
    // property. Returns the propagated error so callers can still choose to
    // handle it.
    const handle402 = useCallback((err, context) => {
        const status = err?.response?.status;
        if (status !== 402) return false;
        const detail = err?.response?.data?.detail;
        const code = typeof detail === 'object' ? detail.code : 'manual';
        const msg = typeof detail === 'object' ? detail.message : 'Upgrade required';
        // Look up the property the user was acting on, if any.
        let property = context?.projectId
            ? state.projects.find(p => p.id === context.projectId)
            : null;
        // Special case — Foundation trying to add a 2nd property hits 402
        // without a property context. Offer them the £14.99 unlock for a
        // LOCKED existing property if they have one (which also promotes
        // them to Rectify and lifts the cap).
        // CRITICAL: never fall back to an already-unlocked property here —
        // the backend correctly returns `{already_unlocked: true}` and the
        // user gets stuck in a loop where clicking pay does nothing.
        // If every property is already unlocked we leave `property = null`
        // so the modal falls back to the lifetime CTA only — and we self-
        // heal the stale tier state on the way out (the user must already
        // be on Rectify if every property is unlocked, so the 402 itself
        // shouldn't have fired).
        if (code === 'property_cap_reached' && !property && state.projects.length > 0) {
            property = state.projects.find(p => !p.is_unlocked) || null;
            if (!property) {
                ent?.refresh?.();
            }
        }
        upgrade?.show?.({ property, reason: code });
        toast.error(msg);
        return true;
    }, [state.projects, upgrade, ent]);

    // ---------- Projects ----------
    const createProject = async (name, address, developerName = '') => {
        try {
            const res = await axios.post(
                `${API}/properties`,
                { name, address: address || '', developer_name: developerName || '' },
                { withCredentials: true },
            );
            await Promise.all([refresh(), ent?.refresh?.()]);
            return res.data.property;
        } catch (err) {
            if (!handle402(err)) handleApiError(err, 'Could not create property');
        }
    };

    const updateProject = async (projectId, patch) => {
        try {
            await axios.patch(`${API}/properties/${projectId}`, patch, { withCredentials: true });
            await refresh();
        } catch (err) {
            handleApiError(err, 'Could not update property');
        }
    };

    const deleteProject = async (projectId) => {
        try {
            await axios.delete(`${API}/properties/${projectId}`, { withCredentials: true });
            await Promise.all([refresh(), ent?.refresh?.()]);
        } catch (err) {
            handleApiError(err, 'Could not delete property');
        }
    };

    // ---------- Rooms ----------
    const addRoom = async (projectId, name) => {
        const tempId = makeTempId('room');
        const optimisticRoom = { id: tempId, name, _pending: true };
        const { result } = await optimistic({
            nextProjects: (projs) => projs.map(p => p.id !== projectId ? p : ({
                ...p,
                rooms: [...(p.rooms || []), optimisticRoom],
            })),
            type: 'addRoom',
            payload: { projectId, name },
            tempId,
            errorMsg: 'Could not add room',
            run: async () => {
                const res = await axios.post(`${API}/properties/${projectId}/rooms`, { name }, { withCredentials: true });
                return res.data.room;
            },
        });
        return result || optimisticRoom;
    };

    const deleteRoom = async (projectId, roomId) => {
        if (typeof roomId === 'string' && roomId.includes('_temp_')) {
            const next = state.projects.map(p => p.id !== projectId ? p : ({
                ...p,
                rooms: (p.rooms || []).filter(r => r.id !== roomId),
            }));
            setState({ projects: next });
            if (userId) saveCachedProjects(userId, next);
            return;
        }
        await optimistic({
            nextProjects: (projs) => projs.map(p => p.id !== projectId ? p : ({
                ...p,
                rooms: (p.rooms || []).filter(r => r.id !== roomId),
            })),
            type: 'deleteRoom',
            payload: { projectId, roomId },
            tempId: roomId,
            errorMsg: 'Could not delete room',
            run: async () => {
                await axios.delete(`${API}/properties/${projectId}/rooms/${roomId}`, { withCredentials: true });
                return true;
            },
        });
    };

    // ---------- Floorplans ----------
    const addFloorplan = async (projectId, name, imageDataURL) => {
        const blob = dataURLToBlob(imageDataURL);
        if (!blob) throw new Error('Expected a dataURL image');
        const fd = new FormData();
        fd.append('file', blob, `${name || 'floor'}.${blob.type.split('/')[1] || 'jpg'}`);
        fd.append('name', name || 'Floor');
        try {
            const res = await axios.post(
                `${API}/properties/${projectId}/floorplans`,
                fd,
                { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
            );
            await refresh();
            return res.data.floorplan;
        } catch (err) {
            if (!handle402(err, { projectId })) handleApiError(err, 'Could not upload floorplan');
        }
    };

    const renameFloorplan = async (projectId, floorId, name) => {
        try {
            await axios.patch(`${API}/properties/${projectId}/floorplans/${floorId}`, { name }, { withCredentials: true });
            await refresh();
        } catch (err) {
            handleApiError(err, 'Could not rename floorplan');
        }
    };

    const deleteFloorplan = async (projectId, floorId) => {
        try {
            await axios.delete(`${API}/properties/${projectId}/floorplans/${floorId}`, { withCredentials: true });
            await refresh();
        } catch (err) {
            handleApiError(err, 'Could not delete floorplan');
        }
    };

    // ---------- Snags ----------
    const uploadSnagPhoto = async (projectId, snagId, photoDataURL) => {
        const blob = dataURLToBlob(photoDataURL);
        if (!blob) return null;
        const fd = new FormData();
        fd.append('file', blob, `photo.${blob.type.split('/')[1] || 'jpg'}`);
        const res = await axios.post(
            `${API}/properties/${projectId}/snags/${snagId}/photo`,
            fd,
            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return res.data.photoPath;
    };

    const addSnag = async (projectId, snag) => {
        const { photo, ...rest } = snag || {};
        const tempId = makeTempId('snag');
        // Build the optimistic record so the snag list / pin appears
        // immediately, even with no signal.
        const optimisticSnag = {
            id: tempId,
            ...rest,
            photo: photo || null,        // local dataURL preview until real upload
            photoPath: null,
            status: rest.status || 'outstanding',
            _pending: true,
            created_at: new Date().toISOString(),
        };
        const { result, projects, queued } = await optimistic({
            nextProjects: (projs) => projs.map(p => p.id !== projectId ? p : ({
                ...p,
                snags: [...(p.snags || []), optimisticSnag],
            })),
            type: 'addSnag',
            payload: { projectId, snag: { ...rest, photo } },
            tempId,
            errorMsg: 'Could not add defect',
            run: async () => {
                const res = await axios.post(`${API}/properties/${projectId}/snags`, rest, { withCredentials: true });
                const created = res.data.snag;
                if (photo) {
                    try { await uploadSnagPhoto(projectId, created.id, photo); }
                    catch { toast.error('Snag saved but photo failed to upload.'); }
                }
                return created;
            },
        });
        if (!queued) maybeNudgeFoundation(projectId, projects);
        return result || optimisticSnag;
    };

    const maybeNudgeFoundation = useCallback((projectId, projs) => {
        if (!projectId) return;
        if (ent?.ready && (ent.tier === 'architect' || ent.tier === 'surveyor')) return;
        const proj = (projs || []).find(p => p.id === projectId);
        if (!proj || proj.is_unlocked) return;
        const count = proj.snags.length;
        const cap = ent?.foundationSnagCap || 10;
        // Nudge on every addition from (cap - 3) up to the cap - so a 10-cap
        // project warns on #7, #8 and #9 (not just once).
        if (count < cap - 3 || count >= cap) return;
        const key = `defectr:nudged:${projectId}:${count}`;
        if (localStorage.getItem(key)) return;
        localStorage.setItem(key, '1');
        toast(`You're at ${count}/${cap} on the free tier. Upgrade to Rectify to unlock unlimited defects and share with your builder.`, {
            duration: 8000,
            action: {
                label: 'Upgrade',
                onClick: () => upgrade?.show?.({ property: proj, reason: 'manual' }),
            },
        });
    }, [ent, upgrade]);

    const updateSnag = async (projectId, snagId, patch) => {
        const { photo, ...rest } = patch || {};
        // Block updates against an optimistic record that hasn't synced yet —
        // the server doesn't know about it. Tell the user to wait for sync.
        if (typeof snagId === 'string' && snagId.includes('_temp_') && !navigator.onLine) {
            toast.error('Reconnect to sync this defect before editing it.');
            return;
        }
        await optimistic({
            nextProjects: (projs) => projs.map(p => p.id !== projectId ? p : ({
                ...p,
                snags: (p.snags || []).map(s => s.id !== snagId ? s : ({
                    ...s,
                    ...rest,
                    ...(photo ? { photo } : {}),
                    _pending: true,
                })),
            })),
            type: 'updateSnag',
            payload: { projectId, snagId, patch: { ...rest, photo } },
            tempId: snagId,
            errorMsg: 'Could not update defect',
            run: async () => {
                if (Object.keys(rest).length > 0) {
                    await axios.patch(`${API}/properties/${projectId}/snags/${snagId}`, rest, { withCredentials: true });
                }
                if (photo) {
                    try { await uploadSnagPhoto(projectId, snagId, photo); }
                    catch { toast.error('Update saved but photo failed to upload.'); }
                }
                return true;
            },
        });
    };

    const deleteSnag = async (projectId, snagId) => {
        // Optimistic record never made it to the server — just drop it locally.
        if (typeof snagId === 'string' && snagId.includes('_temp_')) {
            const next = state.projects.map(p => p.id !== projectId ? p : ({
                ...p,
                snags: (p.snags || []).filter(s => s.id !== snagId),
            }));
            setState({ projects: next });
            if (userId) saveCachedProjects(userId, next);
            return;
        }
        await optimistic({
            nextProjects: (projs) => projs.map(p => p.id !== projectId ? p : ({
                ...p,
                snags: (p.snags || []).filter(s => s.id !== snagId),
            })),
            type: 'deleteSnag',
            payload: { projectId, snagId },
            tempId: snagId,
            errorMsg: 'Could not delete defect',
            run: async () => {
                await axios.delete(`${API}/properties/${projectId}/snags/${snagId}`, { withCredentials: true });
                return true;
            },
        });
    };

    /** Trigger AI analysis for a single snag. Returns { analysis, cached }.
     *  Surfaces 402 via the upgrade modal for Foundation cap hits. */
    const analyseSnag = async (projectId, snagId) => {
        try {
            const res = await axios.post(
                `${API}/properties/${projectId}/snags/${snagId}/analyse`,
                {},
                { withCredentials: true },
            );
            await refresh();  // picks up snag.ai_analysis on next read
            await ent?.refresh?.();  // update aiAnalysisUsed counter
            return res.data;
        } catch (err) {
            if (!handle402(err, { projectId })) handleApiError(err, 'AI analysis failed');
            throw err;
        }
    };

    return {
        ready,
        state,
        refresh,
        createProject, updateProject, deleteProject,
        addRoom, deleteRoom,
        addFloorplan, renameFloorplan, deleteFloorplan,
        addSnag, updateSnag, deleteSnag, analyseSnag,
    };
}
